import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import './Statistics.css'; // Reuse existing styles

interface StatisticsData {
    submissions: any[];
    stats: {
        count: number;
        totalSpending: number;
        avgSpending: number;
    };
}

const CustomStatisticsPage: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [data, setData] = useState<StatisticsData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

    const filters = location.state?.filters;

    useEffect(() => {
        if (!filters) {
            navigate('/dashboard');
            return;
        }

        const fetchData = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/forms/statistics/custom`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                    body: JSON.stringify(filters)
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch statistics');
                }

                const result = await response.json();
                setData(result);
            } catch (err) {
                console.error('Error fetching statistics:', err);
                setError('Kunne ikke hente statistikk');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [filters, navigate]);

    const handleDownloadPdf = async () => {
        if (!data) return;

        setIsGeneratingPdf(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/forms/statistics/custom/pdf`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ filters })
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `statistikk-rapport-${new Date().toISOString().split('T')[0]}.pdf`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            } else {
                alert('Kunne ikke generere PDF');
            }
        } catch (error) {
            console.error('Error downloading PDF:', error);
            alert('Feil ved nedlasting av PDF');
        } finally {
            setIsGeneratingPdf(false);
        }
    };

    if (loading) {
        return (
            <div className="statistics-page">
                <Header />
                <div className="container" style={{ paddingTop: '100px', textAlign: 'center' }}>
                    <h2>Genererer statistikk...</h2>
                </div>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="statistics-page">
                <Header />
                <div className="container" style={{ paddingTop: '100px', textAlign: 'center' }}>
                    <h2>Feil: {error}</h2>
                    <button className="btn-primary" onClick={() => navigate('/dashboard')}>Tilbake til oversikt</button>
                </div>
            </div>
        );
    }

    return (
        <div className="statistics-page">
            <Header />
            <div className="statistics-content">
                <div className="container">
                    <div className="page-header">
                        <div>
                            <h1>Egendefinert Statistikk</h1>
                            <p className="subtitle">Viser resultater basert på dine valgte filtre</p>
                        </div>
                        <div className="header-actions">
                            <button className="btn-secondary" onClick={() => navigate('/dashboard')}>
                                Tilbake
                            </button>
                            <button
                                className="btn-primary"
                                onClick={handleDownloadPdf}
                                disabled={isGeneratingPdf}
                            >
                                {isGeneratingPdf ? 'Genererer PDF...' : 'Last ned PDF'}
                            </button>
                        </div>
                    </div>

                    {/* Key Metrics */}
                    <div className="stats-grid">
                        <div className="stat-card">
                            <h3>Antall skjemaer</h3>
                            <div className="stat-value">{data.stats.count}</div>
                        </div>
                        <div className="stat-card">
                            <h3>Total Spending</h3>
                            <div className="stat-value">{data.stats.totalSpending.toLocaleString()} kr</div>
                        </div>
                        <div className="stat-card">
                            <h3>Snitt Spending</h3>
                            <div className="stat-value">{data.stats.avgSpending.toLocaleString()} kr</div>
                        </div>
                    </div>

                    {/* Filter Summary */}
                    <div className="filter-summary-card" style={{ background: '#eef6f9', padding: '20px', borderRadius: '8px', marginBottom: '30px' }}>
                        <h3 style={{ fontSize: '16px', marginBottom: '10px', color: '#005379' }}>Aktive filtre:</h3>
                        <div className="filter-tags">
                            {filters.facebookInstagramEnabled && <span className="tag">Facebook/Instagram</span>}
                            {filters.linkedinEnabled && <span className="tag">LinkedIn</span>}
                            {filters.minSpending && <span className="tag">Min. spending: {filters.minSpending}</span>}
                            {filters.maxSpending && <span className="tag">Maks. spending: {filters.maxSpending}</span>}
                            {filters.geotargeting && <span className="tag">Geotargeting: {filters.geotargeting}</span>}
                            {filters.annonseformat && <span className="tag">Format: {filters.annonseformat}</span>}
                            {filters.dateFrom && <span className="tag">Fra: {filters.dateFrom}</span>}
                            {filters.dateTo && <span className="tag">Til: {filters.dateTo}</span>}
                        </div>
                    </div>

                    {/* Submissions List */}
                    <div className="submissions-list">
                        <h2>Inkluderte skjemaer</h2>
                        <div className="table-container">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Dato</th>
                                        <th>Kunde</th>
                                        <th>Selger</th>
                                        <th>Kanaler</th>
                                        <th>Spending</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.submissions.map((sub: any) => (
                                        <tr key={sub._id}>
                                            <td>{new Date(sub.submittedAt).toLocaleDateString('no-NO')}</td>
                                            <td>{sub.annonsor}</td>
                                            <td>{sub.userId ? `${sub.userId.firstName} ${sub.userId.lastName}` : 'Ukjent'}</td>
                                            <td>
                                                {[
                                                    sub.formData.facebookInstagramEnabled ? 'FB' : null,
                                                    sub.formData.linkedinEnabled ? 'LI' : null
                                                ].filter(Boolean).join(', ')}
                                            </td>
                                            <td>{sub.calculatedSpending.toLocaleString()} kr</td>
                                        </tr>
                                    ))}
                                    {data.submissions.length === 0 && (
                                        <tr>
                                            <td colSpan={5} style={{ textAlign: 'center', padding: '20px' }}>Ingen skjemaer funnet</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CustomStatisticsPage;
